import React, {
  useState,
  useEffect,
  useCallback,
  createContext,
  useContext,
  useMemo,
} from "react";
import Keycloak, { KeycloakConfig } from "keycloak-js";
import { EuiButton } from "@elastic/eui";
import * as Sentry from "@sentry/react";
import { useLocation } from "react-router";

import {
  AnketaActions,
  AnketaAuthUser,
  AnketaResources,
  AnketaRolesWithPermissions,
  KeycloakContextProviderProps,
  TKeycloakContext,
  AnketaCheckPermissionOptions,
} from "../types";
import { useApi } from "src/api";
import { WarningPage } from "src/pages";
import { StatusPage } from "src/pages/status";
import { hasPermission } from "../functions";
import { useAnketaI18n } from "src/internationalization";
const KeycloakClient = "anketa-demo";
export const KeycloakContext = createContext<TKeycloakContext>(undefined);

const defaultKeycloakConfig: KeycloakConfig = {
  url: "https://keycloak.autonubil.net/auth",
  realm: "autonubil",
  clientId: KeycloakClient,
};

export const useAnketaAuth = (): TKeycloakContext => {
  const context = useContext(KeycloakContext);
  if (!context) {
    throw new Error(
      "useAnketaAuthContext must be used within a KeycloakProvider"
    );
  }
  return context;
};

const useAnketaAuthProvider = () => {
  const { Get } = useApi();
  const [keycloak, setKeycloak] = useState<Keycloak | undefined>(undefined);
  const [token, setToken] = useState<string>(null);

  const [user, setUser] = useState<AnketaAuthUser | undefined>(undefined);
  const [permissionMatrix, setPermissionMatrix] = useState<
    AnketaRolesWithPermissions | undefined
  >(undefined);
  // Surfaces a failed `/auth/me` load as a visible, retryable error instead of a
  // silent blank screen (user stays undefined -> every checkPermission is false).
  const [authError, setAuthError] = useState<string | null>(null);
  const [authRetry, setAuthRetry] = useState(0);
  const retryAuth = () => setAuthRetry((n) => n + 1);
  const { pathname } = useLocation();

  const initKeycloak = useCallback(
    (cfg: any): Keycloak => {
      const kc = new Keycloak(cfg);

      kc.onAuthSuccess = () => {
        kc.updateToken(30).catch((err: any) => {
          console.error("[Keycloak]", err);
        });
      };

      kc.onAuthError = (errorData) => {
        console.warn("[Keycloak] auth error", JSON.stringify(errorData));
      };

      kc.onTokenExpired = () => {
        kc.updateToken(30).catch((err) => {
          console.error("[Keycloak] Token error", err);
        });
      };

      kc.init({
        responseMode: "fragment",
        flow: "standard",
        enableLogging: true,
      })
        .then(async (authenticated) => {
          setKeycloak(kc);
          if (kc.token) {
            setToken(kc.token);
          }
          if (!authenticated) {
            // /security is the published vulnerability-disclosure policy
            // (security.txt points at it, work item opsanio/documentation#333).
            // A reporter arriving from that record has no account here, so an
            // SSO redirect would make the endpoint useless for the one audience
            // it exists for.
            if (
              !pathname.startsWith("/public/") &&
              !pathname.startsWith("/home") &&
              !pathname.startsWith("/security") &&
              pathname !== "/"
            ) {
              await kc.login();
            }
          }
        })
        .catch((err: any) => {
          console.warn("[Keycloak] profile error", err);
        });

      return kc;
    },
    [pathname]
  );

  useEffect(() => {
    if (!keycloak) {
      initKeycloak(defaultKeycloakConfig);
    } else if (!user && keycloak && keycloak.token) {
      setToken(keycloak.token);
    }
  }, [keycloak, initKeycloak]);

  useEffect(() => {
    if (token && token !== null) {
      setAuthError(null);
      Get<AnketaAuthUser>({
        path: "v2/auth/me",
        responseType: "json",
        token: keycloak.token,
      })
        .then((response) => {
          const fetchedUser = response.data;
          setUser(fetchedUser);
          Sentry.setUser({
            email: fetchedUser?.email,
            username: fetchedUser?.username,
          });
        })
        .catch((err) => {
          console.error(err);
          // Don't leave the app silently blank: surface a retryable error.
          setUser(undefined);
          setAuthError(
            err?.message ? String(err.message) : "Failed to load your account."
          );
        });

      // Fetch the live ABAC matrix so UI affordances match the admin-edited
      // permissions. Best-effort: on failure checkPermission falls back to the
      // hardcoded ROLES defaults (the server still enforces the real matrix).
      Get<any>({
        path: "v2/auth/permissions",
        responseType: "json",
        token: keycloak.token,
      })
        .then((response) => {
          const matrix = (response?.data as any)?.data;
          if (matrix) {
            setPermissionMatrix(matrix as AnketaRolesWithPermissions);
          }
        })
        .catch((err) => {
          console.error(err);
        });
    }
  }, [token, authRetry]);
  const checkPermission = (
    resource: AnketaResources,
    action: AnketaActions,
    options?: AnketaCheckPermissionOptions<AnketaResources>
  ): boolean => {
    if (user) {
      return hasPermission(
        user,
        resource as any,
        action as any,
        options,
        permissionMatrix
      );
    } else {
      return false;
    }
  };
  return {
    keycloak,
    user,
    checkPermission,
    permissionMatrix,
    authError,
    retryAuth,
  };
};

export const AnketaAuth = ({
  children,
}: KeycloakContextProviderProps): JSX.Element => {
  const {
    keycloak,
    user,
    checkPermission,
    permissionMatrix,
    authError,
    retryAuth,
  } = useAnketaAuthProvider();
  const { translate } = useAnketaI18n();

  const contextValue = useMemo(
    () => ({ keycloak, user, checkPermission, permissionMatrix }),
    [keycloak, user, permissionMatrix]
  );

  return (
    <KeycloakContext.Provider value={contextValue}>
      <Sentry.ErrorBoundary
        // Last resort for a crash OUTSIDE a page (the layouts catch page
        // crashes themselves and keep header + side nav).
        fallback={({ error, eventId, resetError }: any) => (
          <StatusPage kind="error" error={error} eventId={eventId} onRetry={resetError} />
        )}
        showDialog
        dialogOptions={{
          user: {
            email: user?.email,
            name: user?.username,
          },
        }}
      >
        {authError ? (
          <WarningPage
            error={authError}
            actions={
              <EuiButton color="primary" fill onClick={retryAuth}>
                {translate("anketa.action.retry", {
                  defaultMessage: "Retry",
                })}
              </EuiButton>
            }
          />
        ) : (
          children
        )}
      </Sentry.ErrorBoundary>
    </KeycloakContext.Provider>
  );
};
